<i18n>
[
    "browse__curationTray--displayLink",
    "browse__curationTray--productTileLink",
]
</i18n>

<template>
    <div :style="style">
        <ProductTray
            :id="id"
            ref="productTrayRef"
            :listId="listId"
            :listName="listName"
            :contentModuleId="contentModuleId"
            :contentTitle="contentTitle"
            :products="products"
            :headerLink="trayHeaderLink"
            :headerLinkPlacement="headerLinkPlacement"
            :headerTitle="headerTitle"
            :headerSubtitle="headerSubtitle"
            :totalProducts="totalProducts"
            :mobilePeek="mobilePeek"
            :mobilePeekPercent="mobilePeekPercent"
            :mediumPeekPercent="mediumPeekPercent"
            :isFullBleedDesktop="true"
            :displayCounter="false"
            :desktopSlideSize="desktopSlideSize"
            :xlDesktopSlideSize="xlDesktopSlideSize"
            :mediumSlideSize="mediumSlideSize"
            :mobileSlideSize="mobileSlideSize"
            :translucentButtons="translucentButtons"
            :lightButtons="lightButtons"
            :lightInfoContainer="lightInfoContainer"
            :catalogResponseId="catalogResponseId"
            :showProductInfo="showProductInfo"
            :filters="recommendationQuery"
            :analyticsType="analyticsType"
            :traceId="traceId"
            :viewAllTileLink="viewAllTileLink"
            :faceOutImage="faceOutImage"
            :selectedCuration="selectedCuration"
            :randomizeCurationNavItems="randomizeCurationNavItems"
            :hasEnteredViewport="hasEnteredViewport"
            :isLoaded="isLoaded"
            :requireMinNumberOfProducts="true"
        >
            <template #below-header-content>
                <CurationSubNav
                    :contentfulCurationNavItems="modifiedCurationNavItems"
                    :shouldShowViewAll="false"
                    :updateCuration="updateCurationProducts"
                    :randomizeCurationNavItems="randomizeCurationNavItems"
                    :contentTitle="contentTitle"
                    :contentModuleId="contentModuleId"
                    :hasEnteredViewport="hasEnteredViewport"
                    class="c-curation-products-tray__sub-nav"
                />
            </template>
        </ProductTray>
    </div>
</template>

<script setup>
import { isEmpty, merge } from 'lodash-es';
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useState, useGetters } from '~coreModules/core/js/composables/vuexHelpers';
import { useR15Svc } from '~coreModules/core/js/composables/useR15Svc';
import { useLogger } from '~coreModules/core/js/composables/useLogger';

import { CATALOG_QUERY_IN_STOCK, CATALOG_QUERY_SIZE } from '~coreModules/catalog/js/catalog-constants';
import { PRODUCT_TRAY_HEADER_LINK_PLACEMENTS } from '~coreModules/core/js/constants';
import { CAROUSEL_IDS } from '~modules/analytics/js/analytics-constants';

import { SITE_SETTINGS_MODULE_NAME } from '~coreModules/site-settings/js/site-settings-store';
import { PROFILE_MODULE_NAME } from '~modules/profile/js/profile-store';

import { getDefaultHeaderLink } from '~modules/catalog/js/catalog-utils';

import { getCatalogItems } from '~routes/browse/js/browse-api';

import productTrayProps from '~coreModules/core/js/ui/productTrayProps';

import ProductTray from '~modules/core/components/ui/ProductTray.vue';
import CurationSubNav from '~modules/catalog/components/CurationSubNav.vue';

const { t } = useI18n();
const r15Svc = useR15Svc();
const logger = useLogger();

const props = defineProps({
    ...productTrayProps,
    categorySlug: {
        type: String,
        required: true,
    },
    categoryQuery: {
        type: Object,
        default: null,
    },
    maxProducts: {
        type: Number,
        default: 15,
    },
    shouldShowHeaderLink: {
        type: Boolean,
        default: true,
    },
    lightInfoContainer: {
        type: Boolean,
        default: true,
    },
    paddingBottom: {
        type: String,
        default: null,
    },
    traceId: {
        type: String,
        default: null,
    },
    curationNavItems: {
        type: Array,
        default: () => [],
    },
    randomizeCurationNavItems: {
        type: Boolean,
        default: false,
    },
    omitViewAll: {
        type: Boolean,
        default: false,
    },
    hasEnteredViewport: {
        type: Boolean,
        default: false,
    },
});

const products = ref([]);
// eslint-disable-next-line vue/no-setup-props-reactivity-loss
const totalProducts = ref(props.maxProducts);
const selectedCuration = ref(null);
const catalogResponseId = ref(null);
const productTrayRef = ref(null);
const defaultSortOption = ref(null);
const isLoaded = ref(false);

const headerLinkPlacement = PRODUCT_TRAY_HEADER_LINK_PLACEMENTS.UNDER_CONTENT;
const analyticsType = CAROUSEL_IDS.CURATION_TRAY;

const {
    applyAvailabilityFilterToTrays,
    applySizeFiltersToTrays,
    applyAvailabilityFilterPreference,
} = useState(SITE_SETTINGS_MODULE_NAME, {
    applyAvailabilityFilterToTrays: state => state.contentfulAppConfig?.applyAvailabilityFilterToTrays,
    applySizeFiltersToTrays: state => state.contentfulAppConfig?.applySizeFiltersToTrays,
    applyAvailabilityFilterPreference: state => state.preferences?.applyAvailabilityFilterPreference,
});

const { preferredSizeList } = useGetters(PROFILE_MODULE_NAME, ['preferredSizeList']);
const { catalogFilterWhitelist } = useGetters(SITE_SETTINGS_MODULE_NAME, ['catalogFilterWhitelist']);

const sortMethod = computed(() => (
    props.curationNavItems.find(item => item.slug === selectedCuration.value)?.curationSortMethod
));

const modifiedCurationNavItems = computed(() => {
    if (!props.randomizeCurationNavItems) return props.curationNavItems;

    const newCurationNavItems = [...props.curationNavItems];
    const randomIndex = Math.floor(Math.random() * newCurationNavItems.length);
    const randomItem = newCurationNavItems[randomIndex];
    newCurationNavItems.splice(randomIndex, 1);
    newCurationNavItems.unshift(randomItem);

    return newCurationNavItems;
});

const trayLinkPath = computed(() => {
    const link = getDefaultHeaderLink(props.categorySlug);

    link.query = {
        curation: selectedCuration.value,
        sort: sortMethod.value || defaultSortOption.value?.name,
    };

    return link;
});

const trayHeaderLink = computed(() => {
    if (props.omitViewAll) {
        return null;
    }

    return {
        linkPath: trayLinkPath.value,
        linkTitle: t('browse__curationTray--displayLink'),
    };
});

const viewAllTileLink = computed(() => {
    if (props.omitViewAll) {
        return null;
    }

    return {
        linkPath: trayLinkPath.value,
        linkTitle: t('browse__curationTray--productTileLink'),
    };
});

const recommendationQuery = computed(() => {
    let query = {
        itemsPerPage: props.maxProducts,
    };

    if (props.id) {
        query.contextIds = props.id;
    }

    if (props.applySizePreferencesAndAvailability) {
        if (applyAvailabilityFilterToTrays.value && applyAvailabilityFilterPreference.value) {
            query[CATALOG_QUERY_IN_STOCK] = 'true';
        }

        if (applySizeFiltersToTrays.value && !isEmpty(preferredSizeList.value)) {
            query[CATALOG_QUERY_SIZE] = preferredSizeList.value;
        }
    }

    if (!isEmpty(props.categoryQuery)) {
        query = merge(query, props.categoryQuery);
    }

    return query;
});

const style = computed(() => {
    if (props.paddingBottom) {
        return {
            paddingBottom: props.paddingBottom,
        };
    }

    return {};
});

async function getCurationProducts(curationItem) {
    try {
        const { slug, curationSortMethod } = curationItem;

        const {
            products: returnedProducts,
            responseId,
            sortOptions: returnedSortOptions,
        } = await getCatalogItems({
            r15Svc,
            catalogSlug: slug,
            query: {
                ...recommendationQuery.value,
                ...(curationSortMethod && { sort: curationSortMethod }),
            },
            catalogFilterWhitelist: catalogFilterWhitelist.value,
            logger,
        });

        if (returnedProducts.length < props.maxProducts) {
            totalProducts.value = returnedProducts.length;
        }

        selectedCuration.value = slug;
        catalogResponseId.value = responseId;
        products.value = returnedProducts;
        defaultSortOption.value = returnedSortOptions?.[0];
    } catch (error) {
        logger.debugError(`Failed to fetch curation items for ${curationItem.slug}: `, error);
        Promise.reject(error);
    } finally {
        isLoaded.value = true;
    }
}

async function updateCurationProducts(curationNavItem) {
    isLoaded.value = false;
    products.value = [];
    catalogResponseId.value = null;
    productTrayRef.value?.resetCarousel();
    await getCurationProducts(curationNavItem);
}

watch(() => props.hasEnteredViewport, async (newValue, oldValue) => {
    if (!oldValue && newValue) {
        await getCurationProducts(modifiedCurationNavItems.value[0]);
    }
}, { immediate: true });
</script>

<style lang="scss">
.c-curation-products-tray {
    &__sub-nav {
        padding: $nu-spacer-1 0 $nu-spacer-2pt5;

        @include breakpoint(large) {
            padding: $nu-spacer-2pt5 0 $nu-spacer-2pt5;
        }
    }
}
</style>
